import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Loader } from "../../components/core/Loader/Loader";
import {
  BoardButtonInputs,
  BoardButtonStyle,
} from "../../types/app/boardButton.types";
import { dictionary } from "../../utils/core/dictionary";
import http from "../../api/core/api";
import { apiUrl } from "../../api/core/apiUrl";
import { useParams } from "react-router-dom";
import { Board } from "../../types/app/board.types";

const styleOptions: BoardButtonStyle[] = [
  "Default",
  "Orange",
  "Gray",
  "Green",
  "Amber",
  "Red",
  "Blue",
];

interface Props {
  onSubmit: SubmitHandler<BoardButtonInputs>;
  defaultValues?: BoardButtonInputs;
  onBack: any;
  readOnly: boolean;
}

export function BoardButtonForm({
  onSubmit,
  defaultValues,
  onBack,
  readOnly,
}: Props) {
  const {
    handleSubmit,
    reset,
    register,
    control,
    formState: { errors },
  } = useForm<BoardButtonInputs>({
    defaultValues: {
      style: "Default",
      openInNewTab: false,
      order: 0,
    },
  });
  const { parentId } = useParams();

  const [board, setBoard] = useState<Board | null>(null);

  const fetchBoard = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.BOARD_ITEM}?id=${parentId}`,
    });
    setBoard(response?.data);
  };

  useEffect(() => {
    fetchBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!parentId) return;
    if (defaultValues) {
      reset({ ...defaultValues, boardId: Number(parentId) });
    } else {
      reset({
        id: 0,
        boardId: Number(parentId),
        label: "",
        url: "",
        style: "Default",
        order: 0,
        openInNewTab: false,
        tooltip: "",
      });
    }
  }, [defaultValues, reset, parentId]);

  if (!board) return <Loader />;
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={dictionary.BOARD}
              value={board.name}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={dictionary.BOARD_BUTTON_LABEL}
              required
              disabled={readOnly}
              error={!!errors.label}
              helperText={errors.label?.message}
              {...register("label", { required: "Label is required" })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={dictionary.BOARD_BUTTON_URL}
              required
              disabled={readOnly}
              error={!!errors.url}
              helperText={errors.url?.message}
              {...register("url", { required: "URL is required" })}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="style"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="board-button-style-label">
                    {dictionary.BOARD_BUTTON_STYLE}
                  </InputLabel>
                  <Select
                    labelId="board-button-style-label"
                    label={dictionary.BOARD_BUTTON_STYLE}
                    disabled={readOnly}
                    {...field}
                  >
                    {styleOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label={dictionary.BOARD_BUTTON_ORDER}
              disabled={readOnly}
              {...register("order", { valueAsNumber: true })}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="openInNewTab"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={readOnly}
                    />
                  }
                  label={dictionary.BOARD_BUTTON_OPEN_IN_NEW_TAB}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={dictionary.BOARD_BUTTON_TOOLTIP}
              disabled={readOnly}
              {...register("tooltip")}
            />
          </Grid>
          <Grid item>
            <Stack spacing={2} direction="row">
              <Button type="button" variant="outlined" onClick={onBack}>
                {dictionary.BACK}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={readOnly}
              >
                {dictionary.SAVE}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
